import { PenTool, Zap, Target, TrendingUp, ArrowRight } from 'lucide-react';

const AdFoundryPage = () => {
  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="adfoundry-page">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#FDFBF7]/90 backdrop-blur-md border-b border-[#E8E3D8]" data-testid="navigation">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <div className="flex items-center justify-between h-16 sm:h-20">
            <a href="https://temporarysatisfaction.com" className="font-heading text-xl sm:text-2xl font-semibold text-[#2A2A2A] hover:text-[#5C5C5A] transition-colors" data-testid="nav-logo">
              Temporary Satisfaction
            </a>
            <div className="hidden md:flex items-center gap-8">
              <a href="https://temporarysatisfaction.com" className="text-sm font-medium text-[#5C5C5A] hover:text-[#2A2A2A] transition-colors" data-testid="nav-main-site">Main Site</a>
              <a href="/technology" className="text-sm font-medium text-[#5C5C5A] hover:text-[#2A2A2A] transition-colors" data-testid="nav-platform">Platform</a>
              <a href="/mystoryworld" className="text-sm font-medium text-[#5C5C5A] hover:text-[#2A2A2A] transition-colors" data-testid="nav-mystoryworld">MyStoryWorld</a>
              <a href="/partners" className="text-sm font-medium text-[#5C5C5A] hover:text-[#2A2A2A] transition-colors" data-testid="nav-partners">Partner With Us</a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 sm:pt-40 pb-24 sm:pb-32 relative overflow-hidden" data-testid="hero-section">
        <div className="absolute top-20 right-0 w-[500px] h-[500px] bg-[#C2D1C2]/30 blob" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[#EBDCB2]/30 blob" style={{ animationDelay: '-3s' }} />
        
        <div className="max-w-7xl mx-auto px-6 sm:px-8 relative">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="animate-fade-in-up">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#C2D1C2]/30 text-sm font-medium text-[#2A2A2A] mb-6">
                <PenTool className="w-4 h-4" strokeWidth={1.5} />
                Creator & Business Tools
              </div>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.1] text-[#2A2A2A] mb-6" data-testid="hero-title">
                TS Ad Foundry
              </h1>
              <p className="text-lg sm:text-xl text-[#5C5C5A] leading-relaxed mb-10 max-w-xl" data-testid="hero-description">
                Ethical, automated content generation for creators and small businesses. Empowering entrepreneurs to create professional marketing content without the complexity.
              </p>
              <div className="flex flex-wrap gap-4">
                <a href="/partners" className="btn-primary flex items-center gap-2" data-testid="hero-cta-primary">
                  Get Started
                  <ArrowRight className="w-4 h-4" />
                </a>
                <a href="/technology" className="btn-secondary" data-testid="hero-cta-secondary">
                  View Platform
                </a>
              </div>
            </div>
            
            <div className="animate-fade-in-up animation-delay-400 relative">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.pexels.com/photos/17499879/pexels-photo-17499879.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="Modern creative workspace with content creation tools"
                  className="w-full h-[400px] lg:h-[500px] object-cover"
                  data-testid="hero-image"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#FDFBF7]/40 to-transparent" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-24 sm:py-32 bg-white" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <div className="text-center mb-16">
            <span className="overline block mb-6">Capabilities</span>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-[#2A2A2A]" data-testid="features-title">
              Content Creation, Simplified
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8" data-testid="features-grid">
            <div className="card-premium p-8" data-testid="feature-automation">
              <div className="w-14 h-14 rounded-2xl bg-[#EBDCB2]/40 flex items-center justify-center mb-6">
                <Zap className="w-7 h-7 text-[#2A2A2A]" strokeWidth={1.5} />
              </div>
              <h3 className="font-heading text-xl font-medium text-[#2A2A2A] mb-4">
                Automated Generation
              </h3>
              <p className="text-[#5C5C5A] leading-relaxed">
                AI-powered ad content creation that understands your brand voice and audience. Professional results in minutes.
              </p>
            </div>
            
            <div className="card-premium p-8" data-testid="feature-business">
              <div className="w-14 h-14 rounded-2xl bg-[#C2D1C2]/40 flex items-center justify-center mb-6">
                <Target className="w-7 h-7 text-[#2A2A2A]" strokeWidth={1.5} />
              </div>
              <h3 className="font-heading text-xl font-medium text-[#2A2A2A] mb-4">
                Small Business Focused
              </h3>
              <p className="text-[#5C5C5A] leading-relaxed">
                Built for entrepreneurs and small teams who need agency-quality content without agency costs.
              </p>
            </div>
            
            <div className="card-premium p-8" data-testid="feature-scale">
              <div className="w-14 h-14 rounded-2xl bg-[#E8D5D1]/40 flex items-center justify-center mb-6">
                <TrendingUp className="w-7 h-7 text-[#2A2A2A]" strokeWidth={1.5} />
              </div>
              <h3 className="font-heading text-xl font-medium text-[#2A2A2A] mb-4">
                Scalable Support
              </h3>
              <p className="text-[#5C5C5A] leading-relaxed">
                From solo creators to growing teams, our tools scale with your needs and ambitions.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 sm:py-32" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-6 sm:px-8">
          <div className="relative rounded-[2.5rem] overflow-hidden p-12 sm:p-16 lg:p-20" data-testid="cta-container">
            <div className="absolute inset-0 bg-gradient-to-br from-[#C2D1C2]/60 via-[#EBDCB2]/40 to-[#E8D5D1]/30" />
            <div className="absolute inset-0 bg-[#FDFBF7]/40" />
            
            <div className="relative text-center">
              <h2 className="font-heading text-3xl sm:text-4xl font-medium tracking-tight text-[#2A2A2A] mb-6" data-testid="cta-title">
                Ready to Create?
              </h2>
              <p className="text-lg text-[#5C5C5A] mb-10 max-w-xl mx-auto">
                Join creators and small businesses who are scaling their content with TS Ad Foundry.
              </p>
              <a href="/partners" className="btn-primary inline-flex items-center gap-2" data-testid="cta-button">
                Request Access
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-[#E8E3D8]" data-testid="footer">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <div className="flex flex-col gap-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-6">
              <a href="https://temporarysatisfaction.com" className="font-heading text-lg font-semibold text-[#2A2A2A] hover:text-[#5C5C5A] transition-colors">
                Temporary Satisfaction
              </a>
              <div className="flex items-center gap-8 text-sm text-[#5C5C5A]">
                <a href="https://temporarysatisfaction.com" className="hover:text-[#2A2A2A] transition-colors">Return to Temporary Satisfaction</a>
                <a href="/mystoryworld" className="hover:text-[#2A2A2A] transition-colors">MyStoryWorld</a>
                <a href="/care" className="hover:text-[#2A2A2A] transition-colors">MyStoryWorld Care</a>
                <a href="/technology" className="hover:text-[#2A2A2A] transition-colors">Platform</a>
              </div>
            </div>
            <div className="flex flex-col md:flex-row justify-between items-center gap-4 pt-6 border-t border-[#E8E3D8]/50">
              <p className="text-sm text-[#5C5C5A]">Part of the Temporary Satisfaction ecosystem</p>
              <p className="text-sm text-[#5C5C5A]">&copy; {new Date().getFullYear()} Temporary Satisfaction. All rights reserved.</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AdFoundryPage;
